import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, CheckCircle, Clock, MapPin, AlertTriangle, Zap, Calendar } from 'lucide-react';
import { calendarService, projectService, userService, searchCasesAsync } from '../../services/api';
import SearchableSelect from '../common/SearchableSelect';
import AsyncSearchableSelect from '../common/AsyncSearchableSelect';
import SchedulingSuggestions from '../SchedulingSuggestions';
import ReschedulingSuggestions from './ReschedulingSuggestions';
import useModal from '../../hooks/useModal';
import useNotification from '../../hooks/useNotification';
import Notification from '../common/Notification';

const CalendarEventForm = ({
  event = null,
  initialDateTime = null,
  initialCaseId = null,
  onClose,
  onEventCreated,
  onEventUpdated
}) => {
  const { notification, showError, showWarning, hideNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [firmUsers, setFirmUsers] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [showAutoSchedule, setShowAutoSchedule] = useState(false);
  const [schedulingRequest, setSchedulingRequest] = useState(null);
  const [newEventCreated, setNewEventCreated] = useState(false); // Track if new event has been created
  const [createdEventData, setCreatedEventData] = useState(null); // Store created event data
  const [approvedSuggestions, setApprovedSuggestions] = useState([]); // Track approved suggestion IDs

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  // Helper function to format date for datetime-local input (in local timezone)
  const formatForDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initialize form data
  const [formData, setFormData] = useState(() => {
    const now = new Date();
    let defaultStart, defaultEnd;

    if (initialDateTime) {
      // Check if initialDateTime is an object with start and end times (from drag)
      if (initialDateTime.start && initialDateTime.end) {
        defaultStart = new Date(initialDateTime.start);
        defaultEnd = new Date(initialDateTime.end);
      } else {
        // Single time provided (from click), default to 1 hour duration
        defaultStart = new Date(initialDateTime);
        defaultEnd = new Date(defaultStart.getTime() + 60 * 60000); // 1 hour later
      }
    } else {
      // Round to next hour for manual creation
      defaultStart = new Date(now.getTime() + (60 - now.getMinutes()) * 60000);
      defaultEnd = new Date(defaultStart.getTime() + 60 * 60000);
    }

    return {
      title: event?.title || '',
      description: event?.description || '',
      event_type: event?.event_type || 'meeting',
      start_time: event?.start_time ? formatForDateTimeLocal(new Date(event.start_time)) : formatForDateTimeLocal(defaultStart),
      end_time: event?.end_time ? formatForDateTimeLocal(new Date(event.end_time)) : formatForDateTimeLocal(defaultEnd),
      timezone: event?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      all_day: event?.all_day || false,
      location: event?.location || '',
      case_id: event?.case_id || initialCaseId || '',
      project_id: event?.project_id || '',
      prep_time_minutes: event?.prep_time_minutes || 0,
      travel_time_minutes: event?.travel_time_minutes || 0,
      reminder_minutes: event?.reminder_minutes || [15],
      recurrence_type: event?.recurrence_type || 'none',
      recurrence_end_date: event?.recurrence_end_date ? new Date(event.recurrence_end_date).toISOString().slice(0, 10) : '',
      sync_enabled: event?.sync_enabled !== undefined ? event.sync_enabled : true,
      assigned_to_id: event?.assigned_to_id || ''
    };
  });

  // Event types with descriptions
  const eventTypes = [
    { value: 'meeting', label: 'Meeting', icon: '👥' },
    { value: 'court_date', label: 'Court Date', icon: '⚖️' },
    { value: 'deadline', label: 'Deadline', icon: '⏰' },
    { value: 'call', label: 'Phone Call', icon: '📞' },
    { value: 'consultation', label: 'Consultation', icon: '💼' },
    { value: 'research', label: 'Research', icon: '📚' },
    { value: 'task', label: 'Task', icon: '✅' },
    { value: 'other', label: 'Other', icon: '📅' }
  ];

  const recurrenceTypes = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  // Load cases and projects
  useEffect(() => {
    const loadData = async () => {
      try {
        // Reset the new event created flag, approved suggestions, and created event data when form opens
        setNewEventCreated(false);
        setCreatedEventData(null);
        setApprovedSuggestions([]);
        
        const [projectsResponse, usersResponse] = await Promise.all([
          projectService.getProjects(),
          userService.getUsers()
        ]);
        setProjects(projectsResponse.data || []);
        const activeUsers = (usersResponse.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setFirmUsers(activeUsers);
      } catch (error) {
        console.error('Error loading form data:', error);
      }
    };
    loadData();
  }, []);

  // Helper function to calculate duration in minutes
  const getDurationMinutes = useCallback(() => {
    if (!formData.start_time || !formData.end_time) return 60; // Default 1 hour
    const start = new Date(formData.start_time);
    const end = new Date(formData.end_time);
    return Math.max(15, Math.round((end - start) / (1000 * 60))); // Minimum 15 minutes
  }, [formData.start_time, formData.end_time]);

  // Toggle auto-schedule mode
  const toggleAutoSchedule = () => {
    if (!showAutoSchedule) {
      // Create scheduling request when enabling auto-schedule
      const request = {
        title: formData.title || 'New Event',
        duration_minutes: getDurationMinutes(),
        event_type: formData.event_type,
        priority: 'medium', // Default priority
        preferred_date: formData.start_time ? new Date(formData.start_time).toISOString() : new Date().toISOString(),
        requires_business_hours: true,
        case_id: formData.case_id || null,
        project_id: formData.project_id || null,
        description: formData.description,
        location: formData.location
      };
      setSchedulingRequest(request);
    }
    setShowAutoSchedule(!showAutoSchedule);
  };

  // Handle selecting a scheduling suggestion
  const handleSchedulingSuggestion = (suggestion) => {
    const startDate = new Date(suggestion.start_datetime);
    const endDate = new Date(suggestion.end_datetime);
    
    setFormData(prev => ({
      ...prev,
      start_time: formatForDateTimeLocal(startDate),
      end_time: formatForDateTimeLocal(endDate)
    }));
    
    setShowAutoSchedule(false);
  };

  // Handle auto-schedule success
  const handleAutoScheduleSuccess = (result) => {
    if (result.event_id) {
      // Event was auto-created, close the form
      if (onEventCreated) {
        // We'd need to fetch the created event details here
        onEventCreated({ id: result.event_id, ...formData });
      }
      onClose();
    }
  };

  // Update scheduling request when form data changes
  useEffect(() => {
    if (showAutoSchedule) {
      const request = {
        title: formData.title || 'New Event',
        duration_minutes: getDurationMinutes(),
        event_type: formData.event_type,
        priority: 'medium',
        preferred_date: formData.start_time ? new Date(formData.start_time).toISOString() : new Date().toISOString(),
        requires_business_hours: true,
        case_id: formData.case_id || null,
        project_id: formData.project_id || null,
        description: formData.description,
        location: formData.location
      };
      setSchedulingRequest(request);
    }
  }, [formData.title, formData.event_type, formData.start_time, formData.end_time, formData.case_id, formData.project_id, formData.description, formData.location, showAutoSchedule, getDurationMinutes]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear project if case changes
    if (field === 'case_id') {
      setFormData(prev => ({
        ...prev,
        project_id: ''
      }));
    }
  };

  const addReminder = () => {
    if (formData.reminder_minutes.length < 5) {
      setFormData(prev => ({
        ...prev,
        reminder_minutes: [...prev.reminder_minutes, 15]
      }));
    }
  };

  const updateReminder = (index, value) => {
    const newReminders = [...formData.reminder_minutes];
    newReminders[index] = parseInt(value);
    setFormData(prev => ({
      ...prev,
      reminder_minutes: newReminders
    }));
  };

  const removeReminder = (index) => {
    setFormData(prev => ({
      ...prev,
      reminder_minutes: prev.reminder_minutes.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowConflicts(false);

    // Client-side validation: end time must be after start time
    const startTime = new Date(formData.start_time);
    const endTime = new Date(formData.end_time);

    if (endTime <= startTime) {
      showWarning('End time must be after start time');
      setLoading(false);
      return;
    }

    try {
      // Prepare the event data
      const eventData = {
        ...formData,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        recurrence_end_date: formData.recurrence_end_date ? new Date(formData.recurrence_end_date).toISOString() : null,
        case_id: formData.case_id || null,
        project_id: formData.project_id || null,
        assigned_to_id: formData.assigned_to_id || null
      };

      let response;
      if (event) {
        // Update existing event
        response = await calendarService.updateEvent(event.id, eventData);
      } else if (!newEventCreated) {
        // Create new event only if it wasn't already created during conflict resolution
        response = await calendarService.createEvent(eventData);
        setNewEventCreated(true);
      } else {
        // Event was already created during conflict resolution, just close the form
        console.log('Regular submit - event already created, using stored data:', createdEventData);
        if (onEventCreated && createdEventData) {
          onEventCreated(createdEventData); // Use the stored event data
        }
        onClose();
        setLoading(false);
        return;
      }

      if (response.data.success) {
        // Event was created successfully
        setNewEventCreated(true);
        setCreatedEventData(response.data.event);
        
        // Show sync status if applicable
        if (response.data.sync_result) {
          const syncStatus = response.data.sync_result;
          if (syncStatus.success) {
            console.log('✅ Event synced to Google Calendar automatically');
          } else {
            console.log('⚠️ Auto-sync failed:', syncStatus.error || syncStatus.message);
          }
        }
        
        // If there are conflicts, show the rescheduling dialog
        if (response.data.conflicts_detected) {
          setConflicts({
            conflicts: response.data.conflicts,
            rescheduling_suggestions: response.data.rescheduling_suggestions,
            plan_confidence: response.data.plan_confidence,
            total_impact: response.data.total_impact,
            message: response.data.message,
            created_event: response.data.event  // Include the created event data
          });
          setShowConflicts(true);
        } else {
          // No conflicts - just close and notify success
          if (event && onEventUpdated) {
            onEventUpdated(response.data.event);
          } else if (!event && onEventCreated) {
            onEventCreated(response.data.event);
          }
          onClose();
        }
      } else {
        console.error('Failed to save event:', response.data);
        showError('Failed to save event. Please try again.');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      showError('Failed to save event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConflictResolution = async (action) => {
    setLoading(true);
    try {
      // If event was already created, just close the dialog
      if (newEventCreated && createdEventData) {
        console.log('Event already created during initial submit, closing conflict dialog');
        if (onEventCreated) {
          onEventCreated(createdEventData);
        }
        onClose();
        setLoading(false);
        setShowConflicts(false);
        return;
      }

      const eventData = {
        ...formData,
        start_time: new Date(formData.start_time).toISOString(),
        end_time: new Date(formData.end_time).toISOString(),
        recurrence_end_date: formData.recurrence_end_date ? new Date(formData.recurrence_end_date).toISOString() : null,
        case_id: formData.case_id || null,
        project_id: formData.project_id || null
      };

      if (action === 'force') {
        // Force create the event despite conflicts
        eventData.force_create = true;
      } else if (action === 'auto_reschedule') {
        // Auto-reschedule conflicting events
        eventData.auto_reschedule = true;
      }

      let response;
      if (event) {
        response = await calendarService.updateEvent(event.id, eventData);
      } else {
        response = await calendarService.createEvent(eventData);
        setNewEventCreated(true);
        setCreatedEventData(response.data.event);
      }

      if (response.data.success || response.data.event_created) {
        if (event && onEventUpdated) {
          onEventUpdated(response.data.event);
        } else if (!event && onEventCreated) {
          onEventCreated(response.data.event);
        }
        onClose();
      } else {
        console.error('Failed to resolve conflict:', response.data);
        showError('Failed to save event. Please try again.');
      }
    } catch (error) {
      console.error('Error resolving conflict:', error);
      showError('Failed to save event. Please try again.');
    } finally {
      setLoading(false);
      setShowConflicts(false);
    }
  };


  const ConflictDialog = () => {
    const [showAlternatives, setShowAlternatives] = useState(false);
    
    const handleFindAlternatives = async () => {
      const request = {
        title: formData.title,
        duration_minutes: getDurationMinutes(),
        event_type: formData.event_type,
        priority: 'medium',
        preferred_date: new Date(formData.start_time).toISOString(),
        requires_business_hours: true,
        case_id: formData.case_id || null,
        project_id: formData.project_id || null,
        description: formData.description,
        location: formData.location
      };
      setSchedulingRequest(request);
      setShowAlternatives(true);
    };

    const handleAlternativeSelection = async (suggestion) => {
      const startDate = new Date(suggestion.start_datetime);
      const endDate = new Date(suggestion.end_datetime);
      
      // Update form data with new time
      const updatedFormData = {
        ...formData,
        start_time: formatForDateTimeLocal(startDate),
        end_time: formatForDateTimeLocal(endDate)
      };
      
      setFormData(updatedFormData);
      
      // If this is an auto-saved suggestion, the event was already created
      if (suggestion.created && suggestion.event_id) {
        setNewEventCreated(true);
        setCreatedEventData(suggestion);
        if (onEventCreated) {
          onEventCreated({ id: suggestion.event_id, ...updatedFormData });
        }
        onClose();
      } else {
        // Close conflicts dialog and let user submit with new time
        setShowConflicts(false);
      }
      setShowAlternatives(false);
    };

    return (
      <div
        className="fixed inset-0 flex items-center justify-center z-[9999]"
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
      >
        <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-amber-500 mr-2" />
            <h3 className="text-lg font-semibold">Schedule Conflicts Detected</h3>
          </div>
          
          {!showAlternatives ? (
            <>
              <div className="mb-6">
                <p className="text-gray-600 mb-3">
                  {conflicts?.message || "This event conflicts with existing calendar entries:"}
                </p>
                
                {/* Show conflicts */}
                {conflicts?.conflicts && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Conflicts:</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {conflicts.conflicts.map((conflict, index) => (
                        <div key={index} className={`p-3 border rounded ${
                          conflict.severity === 'error' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                        }`}>
                          <div className={`font-medium ${
                            conflict.severity === 'error' ? 'text-red-900' : 'text-yellow-900'
                          }`}>
                            {conflict.conflicting_event}
                          </div>
                          <div className={`text-sm ${
                            conflict.severity === 'error' ? 'text-red-700' : 'text-yellow-700'
                          }`}>
                            {conflict.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show rescheduling suggestions with new calendar day view */}
                {conflicts?.rescheduling_suggestions && conflicts.rescheduling_suggestions.length > 0 && (
                  <ReschedulingSuggestions 
                    reschedulingPlan={{
                      new_event_title: formData.title,
                      new_event_start: formData.start_time,
                      new_event_end: formData.end_time,
                      conflicts_detected: conflicts.conflicts,
                      rescheduling_suggestions: conflicts.rescheduling_suggestions,
                      plan_confidence: conflicts.plan_confidence || 0
                    }}
                    approvedSuggestions={approvedSuggestions}
                    onApprove={async (suggestions) => {
                      // Handle approval of specific suggestions
                      console.log('Approved suggestions:', suggestions);
                      
                      // Create the new event with force flag and apply rescheduling
                      setLoading(true);
                      try {
                        const reschedulingData = suggestions.map(s => ({
                          event_id: s.original_event_id,
                          new_start_time: s.suggested_start || s.suggestedTime?.start_time,
                          new_end_time: s.suggested_end || s.suggestedTime?.end_time
                        }));

                        const eventData = {
                          ...formData,
                          start_time: new Date(formData.start_time).toISOString(),
                          end_time: new Date(formData.end_time).toISOString(),
                          recurrence_end_date: formData.recurrence_end_date ? new Date(formData.recurrence_end_date).toISOString() : null,
                          case_id: formData.case_id || null,
                          project_id: formData.project_id || null,
                          force_create: true, // Force create the new event
                          apply_rescheduling: reschedulingData
                        };

                        // Only create/update the new event if it hasn't been created yet
                        let response;
                        if (!newEventCreated) {
                          const newEventData = { ...eventData };
                          delete newEventData.apply_rescheduling; // Remove unsupported parameter
                          
                          if (event) {
                            response = await calendarService.updateEvent(event.id, newEventData);
                          } else {
                            response = await calendarService.createEvent(newEventData);
                          }
                          setNewEventCreated(true); // Mark as created
                          setCreatedEventData(response.data.event); // Store the created event data
                          console.log('Event created and stored:', response.data.event);
                        } else {
                          // Event already created, just simulate a success response
                          response = { data: { success: true } };
                        }
                        
                        console.log('Create/update response:', response.data);
                        
                        if (response.data.success || response.data.event_created) {
                          // Now apply the rescheduling by updating each conflicting event
                          
                          const reschedulingPromises = reschedulingData.map(async (reschedule) => {
                            try {
                              const updateResponse = await calendarService.updateEvent(reschedule.event_id, {
                                start_time: reschedule.new_start_time,
                                end_time: reschedule.new_end_time
                              });
                              return { success: true, eventId: reschedule.event_id, response: updateResponse };
                            } catch (error) {
                              console.error(`Failed to reschedule event ${reschedule.event_id}:`, error);
                              return { success: false, eventId: reschedule.event_id, error: error };
                            }
                          });
                          
                          // Wait for all rescheduling operations to complete
                          const reschedulingResults = await Promise.all(reschedulingPromises);
                          
                          // Check if any rescheduling failed
                          const failedRescheduling = reschedulingResults.filter(result => !result.success);
                          if (failedRescheduling.length > 0) {
                            console.error('Some rescheduling operations failed:', failedRescheduling);
                            showWarning(`Failed to reschedule ${failedRescheduling.length} event(s). The new event was created but some conflicting events could not be moved.`);
                          }
                          
                          // Check if there are remaining conflicts after approving these suggestions
                          const approvedEventIds = suggestions.map(s => s.original_event_id);
                          
                          // Track approved suggestions for UI display
                          setApprovedSuggestions(prev => [...prev, ...approvedEventIds]);
                          
                          const remainingConflicts = {
                            ...conflicts,
                            rescheduling_suggestions: conflicts.rescheduling_suggestions.filter(
                              suggestion => !approvedEventIds.includes(suggestion.original_event_id)
                            ),
                            conflicts: conflicts.conflicts.filter(
                              conflict => !approvedEventIds.includes(conflict.conflicting_event_id)
                            )
                          };
                          
                          if (remainingConflicts.rescheduling_suggestions.length > 0) {
                            // Update the conflicts state to show only remaining conflicts
                            setConflicts(remainingConflicts);
                            // Keep the modal open - don't call callbacks that would close it
                          } else {
                            // No more conflicts, safe to notify parent and close
                            const eventToReport = response.data?.event || createdEventData;
                            console.log('Final callback - event to report:', eventToReport);
                            console.log('onEventCreated exists:', !!onEventCreated);
                            console.log('event exists (update mode):', !!event);
                            
                            if (event && onEventUpdated) {
                              onEventUpdated(eventToReport);
                            } else if (!event && onEventCreated) {
                              onEventCreated(eventToReport);
                            }
                            setShowConflicts(false);
                            onClose();
                          }
                        } else {
                          console.error('Failed to create new event:', response.data);
                          showError('Failed to save event. Please try again.');
                          setShowConflicts(false);
                        }
                      } catch (error) {
                        console.error('Error applying rescheduling:', error);
                        showError('Failed to save event with rescheduling. Please try again.');
                        setShowConflicts(false);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    onReject={() => {
                      setShowConflicts(false);
                    }}
                    loading={loading}
                    className="mb-4"
                  />
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={() => setShowConflicts(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                
                {conflicts?.rescheduling_suggestions && conflicts.rescheduling_suggestions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleConflictResolution('auto_reschedule')}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Auto-Reschedule
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={handleFindAlternatives}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-md text-sm font-medium hover:bg-yellow-600 flex items-center justify-center"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Find Alternatives
                </button>
                
                <button
                  type="button"
                  onClick={() => handleConflictResolution('force')}
                  disabled={loading}
                  className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                >
                  Force Create
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <h4 className="text-lg font-medium text-gray-900 mb-2">Alternative Time Slots</h4>
                <p className="text-sm text-gray-600">Select a conflict-free time slot:</p>
              </div>
              
              <SchedulingSuggestions
                schedulingRequest={schedulingRequest}
                onSelectSuggestion={handleAlternativeSelection}
                showAutoScheduleButton={false}
                autoSaveOnSelect={true}
                className="mb-4"
              />
              
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowAlternatives(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Back to Conflicts
                </button>
                <button
                  type="button"
                  onClick={() => setShowConflicts(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700"
                >
                  Keep Original Time
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center z-[9999]"
        onMouseDown={handleBackdropMouseDown}
        onMouseUp={handleBackdropMouseUp}
      >
        <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">
              {event ? 'Edit Event' : 'Create New Event'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter event title"
              />
            </div>

            {/* Event Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                value={formData.event_type}
                onChange={(e) => handleInputChange('event_type', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                {eventTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Assign To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assign To
              </label>
              <SearchableSelect
                value={formData.assigned_to_id}
                onChange={(val) => handleInputChange('assigned_to_id', val)}
                options={firmUsers.map(u => ({
                  value: u.id,
                  label: `${u.first_name} ${u.last_name}`
                }))}
                emptyOption="Myself (default)"
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.start_time}
                  onChange={(e) => handleInputChange('start_time', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time *
                </label>
                <input
                  type="datetime-local"
                  required
                  value={formData.end_time}
                  onChange={(e) => handleInputChange('end_time', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>

            {/* All Day Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="all_day"
                checked={formData.all_day}
                onChange={(e) => handleInputChange('all_day', e.target.checked)}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <label htmlFor="all_day" className="ml-2 text-sm text-gray-700">
                All day event
              </label>
            </div>

            {/* Intelligent Scheduling */}
            {!event && ( /* Only show for new events */
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <Zap className="h-5 w-5 text-yellow-600 mr-2" />
                    <h4 className="text-sm font-medium text-gray-900">Intelligent Scheduling</h4>
                  </div>
                  <button
                    type="button"
                    onClick={toggleAutoSchedule}
                    className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      showAutoSchedule 
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                        : 'bg-white text-yellow-600 border border-yellow-500 hover:bg-gray-50'
                    }`}
                  >
                    {showAutoSchedule ? (
                      <>
                        <Calendar className="w-3 h-3 mr-1" />
                        Manual Time
                      </>
                    ) : (
                      <>
                        <Zap className="w-3 h-3 mr-1" />
                        Find Best Time
                      </>
                    )}
                  </button>
                </div>
                
                {showAutoSchedule ? (
                  <SchedulingSuggestions
                    schedulingRequest={schedulingRequest}
                    onSelectSuggestion={handleSchedulingSuggestion}
                    onAutoSchedule={handleAutoScheduleSuccess}
                    showAutoScheduleButton={true}
                    className="mt-3"
                  />
                ) : (
                  <p className="text-sm text-yellow-600">
                    Let AI find the perfect time slot based on your preferences and existing calendar events.
                  </p>
                )}
              </div>
            )}

            {/* Prep Time and Travel Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prep Time (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.prep_time_minutes}
                  onChange={(e) => handleInputChange('prep_time_minutes', parseInt(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Travel Time (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.travel_time_minutes}
                  onChange={(e) => handleInputChange('travel_time_minutes', parseInt(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin className="h-4 w-4 inline mr-1" />
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter location or address"
              />
            </div>

            {/* Case and Project */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Case
                </label>
                <AsyncSearchableSelect
                  value={formData.case_id}
                  onChange={(val) => handleInputChange('case_id', val)}
                  onSearch={searchCasesAsync}
                  emptyOption="Select a case (optional)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Project
                </label>
                <SearchableSelect
                  value={formData.project_id}
                  onChange={(val) => handleInputChange('project_id', val)}
                  options={projects.filter(p => !formData.case_id || p.case_id === formData.case_id).map(project => ({
                    value: project.id,
                    label: project.name
                  }))}
                  emptyOption="Select a project (optional)"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter event description"
              />
            </div>

            {/* Reminders */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="h-4 w-4 inline mr-1" />
                Reminders
              </label>
              <div className="space-y-2">
                {formData.reminder_minutes.map((reminder, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <select
                      value={reminder}
                      onChange={(e) => updateReminder(index, e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value={5}>5 minutes before</option>
                      <option value={15}>15 minutes before</option>
                      <option value={30}>30 minutes before</option>
                      <option value={60}>1 hour before</option>
                      <option value={120}>2 hours before</option>
                      <option value={1440}>1 day before</option>
                      <option value={2880}>2 days before</option>
                      <option value={10080}>1 week before</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeReminder(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addReminder}
                  className="text-gray-600 hover:text-yellow-600 text-sm flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Reminder
                </button>
              </div>
            </div>

            {/* Recurrence */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recurrence
                </label>
                <select
                  value={formData.recurrence_type}
                  onChange={(e) => handleInputChange('recurrence_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  {recurrenceTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {formData.recurrence_type !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recurrence End Date
                  </label>
                  <input
                    type="date"
                    value={formData.recurrence_end_date}
                    onChange={(e) => handleInputChange('recurrence_end_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-md text-sm font-medium hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    {event ? 'Updating...' : 'Creating...'}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {event ? 'Update Event' : 'Create Event'}
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showConflicts && conflicts && <ConflictDialog />}

      {/* Notification Toast */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </>
  );
};

export default CalendarEventForm;